import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = 'http://localhost:5000';

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

/* ── User Avatar (shows profile picture when available) ───────────────── */
const UserAvatar = ({ user, size = 'w-8 h-8', textSize = 'text-xs' }) => {
  const [imgError, setImgError] = useState(false);
  const src = !imgError && user?.profile_picture ? `${API}${user.profile_picture}` : null;
  return (
    <div
      className={`${size} rounded-full flex items-center justify-center text-white font-bold ${textSize} shadow-md overflow-hidden flex-shrink-0 ring-2 ring-white/20`}
      style={{ backgroundColor: src ? 'transparent' : (user?.avatar_color || '#25D366') }}
    >
      {src
        ? <img src={src} alt={user?.name} className="w-full h-full object-cover" onError={() => setImgError(true)} />
        : getInitials(user?.name)
      }
    </div>
  );
};

/* ── SVG icon components (crisp, modern, outline style) ───────────────── */
const IconChat = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
  </svg>
);

const IconFeed = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
  </svg>
);

const IconSchedule = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
  </svg>
);

const IconProfile = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
  </svg>
);

const IconAdmin = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
  </svg>
);

const IconInstall = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
  </svg>
);

const IconLogout = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
  </svg>
);

const IconMenu = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/>
  </svg>
);

const IconClose = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
  </svg>
);

/* ── Nav link definitions ──────────────────────────────────────────────── */
const BASE_LINKS = [
  { to: '/chat',      label: 'Chat',     Icon: IconChat     },
  { to: '/feed',      label: 'Feed',     Icon: IconFeed     },
  { to: '/timetable', label: 'Schedule', Icon: IconSchedule },
  { to: '/profile',   label: 'Profile',  Icon: IconProfile  },
];

/* ── Navbar ────────────────────────────────────────────────────────────── */
const Navbar = () => {
  const { user, logout } = useAuth();
  const location  = useLocation();
  const navigate  = useNavigate();
  const [menuOpen,       setMenuOpen]       = useState(false);
  const [installPrompt,  setInstallPrompt]  = useState(null);
  const [isInstalled,    setIsInstalled]    = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true);
    if (window.__pwaInstallPrompt) setInstallPrompt(window.__pwaInstallPrompt);
    const onAvail    = () => setInstallPrompt(window.__pwaInstallPrompt);
    const onInstall  = () => { setIsInstalled(true); setInstallPrompt(null); };
    window.addEventListener('pwaInstallAvailable', onAvail);
    window.addEventListener('pwaInstalled',        onInstall);
    return () => {
      window.removeEventListener('pwaInstallAvailable', onAvail);
      window.removeEventListener('pwaInstalled',        onInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) { navigate('/install'); return; }
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true); setInstallPrompt(null); window.__pwaInstallPrompt = null;
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const isActive = (p) => location.pathname === p;

  const navLinks = [
    ...BASE_LINKS,
    ...(user?.role === 'admin' ? [{ to: '/admin', label: 'Admin', Icon: IconAdmin }] : []),
  ];

  return (
    <>
      {/* ─────────────────────── Top bar ─────────────────────────────── */}
      <nav className="bg-whatsapp-darker shadow-lg sticky top-0 z-50 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">

            {/* Logo */}
            <Link to="/chat" className="flex items-center gap-2 sm:gap-3 min-w-0 group">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-whatsapp-green to-whatsapp-dark rounded-xl flex items-center justify-center shadow-md flex-shrink-0 group-hover:shadow-whatsapp-green/30 transition-shadow">
                <span className="text-white font-black text-xs sm:text-sm tracking-tight">S3</span>
              </div>
              <div className="hidden xs:block min-w-0">
                <p className="text-white font-bold text-sm sm:text-base leading-tight truncate">GS Mwendo R</p>
                <p className="text-green-400 text-xs leading-none">Class S3</p>
              </div>
            </Link>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map(({ to, label, Icon }) => (
                <Link key={to} to={to}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive(to)
                      ? 'bg-whatsapp-green/20 text-whatsapp-green border border-whatsapp-green/30'
                      : 'text-green-200/80 hover:text-white hover:bg-white/10'
                  }`}>
                  <Icon className="w-4 h-4" />
                  <span>{label}</span>
                </Link>
              ))}
            </div>

            {/* Desktop right */}
            <div className="hidden md:flex items-center gap-2">
              {!isInstalled && (
                <button onClick={handleInstall}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/8 hover:bg-white/15 text-green-300 hover:text-white text-xs font-medium transition-all border border-white/10 hover:border-white/20">
                  <IconInstall className="w-3.5 h-3.5" />
                  Install
                </button>
              )}

              <Link to="/profile" className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-white/10 transition-all group">
                <UserAvatar user={user} size="w-7 h-7" textSize="text-xs" />
                <div>
                  <p className="text-white text-xs font-semibold leading-tight">{user?.name}</p>
                  <p className="text-green-400 text-xxs capitalize leading-none">{user?.role}</p>
                </div>
              </Link>

              <button onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 text-xs font-medium transition-all border border-red-500/20 hover:border-red-400/30">
                <IconLogout className="w-3.5 h-3.5" />
                Logout
              </button>
            </div>

            {/* Mobile: avatar + burger */}
            <div className="flex md:hidden items-center gap-2">
              <Link to="/profile">
                <UserAvatar user={user} size="w-8 h-8" textSize="text-xs" />
              </Link>
              <button onClick={() => setMenuOpen(p => !p)} aria-label="Menu"
                className="text-white p-1.5 rounded-xl hover:bg-white/10 transition-colors">
                {menuOpen ? <IconClose /> : <IconMenu />}
              </button>
            </div>
          </div>

          {/* Mobile dropdown */}
          {menuOpen && (
            <div className="md:hidden pb-3 pt-2 space-y-1 border-t border-white/10">
              <div className="flex items-center gap-3 px-3 py-2 mb-1">
                <UserAvatar user={user} size="w-10 h-10" textSize="text-sm" />
                <div>
                  <p className="text-white text-sm font-semibold">{user?.name}</p>
                  <p className="text-green-400 text-xs capitalize">{user?.role}</p>
                </div>
              </div>

              {navLinks.map(({ to, label, Icon }) => (
                <Link key={to} to={to} onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive(to) ? 'bg-whatsapp-green/20 text-whatsapp-green' : 'text-green-200/80 hover:text-white hover:bg-white/10'
                  }`}>
                  <Icon className="w-4 h-4" />
                  {label}
                </Link>
              ))}

              {!isInstalled && (
                <button onClick={() => { setMenuOpen(false); handleInstall(); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-green-200/80 hover:text-white hover:bg-white/10 transition-all">
                  <IconInstall className="w-4 h-4" />
                  Install App
                </button>
              )}

              <button onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all">
                <IconLogout className="w-4 h-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* ─────────────────── Mobile bottom tab bar ───────────────────── */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-whatsapp-darker/95 backdrop-blur-md border-t border-white/10 pb-safe">
        <div className="flex items-stretch h-14">
          {navLinks.map(({ to, label, Icon }) => {
            const active = isActive(to);
            return (
              <Link key={to} to={to}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 relative transition-colors ${
                  active ? 'text-whatsapp-green' : 'text-green-400/60 hover:text-green-300'
                }`}>
                {active && (
                  <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-whatsapp-green rounded-b-full" />
                )}
                <Icon className={`w-5 h-5 transition-transform ${active ? 'scale-110' : ''}`} />
                <span className="text-xxs font-medium leading-none">{label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Navbar;
